import { api } from '@/lib/axios';

export interface ProductImage {
  id?: string;
  url: string;
  fileName?: string;
  fileSize?: number;
  type?: string;
  order?: number;
  isPrimary?: boolean;
}

export interface Product {
  id: string;
  name: string;
  description?: string;
  sku?: string;
  brand?: string;
  price?: number;
  costPrice?: number;
  stock?: number;
  category?: string;
  tags?: string;
  active: boolean;
  featured: boolean;
  showInStore: boolean;
  createdAt: string;
  updatedAt: string;
  images: ProductImage[];
}

export interface CreateProductDto {
  name: string;
  description?: string;
  sku?: string;
  brand?: string;
  price?: number;
  costPrice?: number;
  stock?: number;
  category?: string;
  tags?: string;
  active?: boolean;
  featured?: boolean;
  showInStore?: boolean;
  images?: ProductImage[];
}

export interface UpdateProductDto extends Partial<CreateProductDto> {}

export interface ProductQueryParams {
  search?: string;
  category?: string;
  activeOnly?: boolean;
  featuredOnly?: boolean;
  page?: number;
  limit?: number;
}

export interface PaginatedProductsResponse {
  data: Product[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export const productsService = {
  async getAll(params?: ProductQueryParams): Promise<PaginatedProductsResponse> {
    const response = await api.get('/products', { params });
    return response.data;
  },

  async getById(id: string): Promise<Product> {
    const response = await api.get(`/products/${id}`);
    return response.data;
  },

  async create(data: CreateProductDto): Promise<Product> {
    const response = await api.post('/products', data);
    return response.data;
  },

  async update(id: string, data: UpdateProductDto): Promise<Product> {
    const response = await api.patch(`/products/${id}`, data);
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/products/${id}`);
  },

  async getCategories(): Promise<string[]> {
    const response = await api.get('/products/categories');
    return response.data;
  },

  async toggleStore(id: string): Promise<Product> {
    const response = await api.patch(`/products/${id}/toggle-store`);
    return response.data;
  },

  async uploadImage(file: File): Promise<ProductImage> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },
};
